<template>
  <div>
    <b-modal
      ref="item-sources-modal"
      title="Sources"
      size="xl"
      @ok="addSource"
      @shown="onInitialize"
    >
      <div class="d-flex flex-column">
        <h6 class="mb-2">Select available parts from a source.</h6>
        <div class="d-flex justify-content-between">
          <div class="d-flex justify-content-start">
            <div class="d-flex align-items-center mr-3">
              <a class="custom-form-label flex-shrink-zero">Part #</a>
              <b-input v-model="partNumber"></b-input>
            </div>

            <div class="d-flex align-items-center mr-3">
              <a class="custom-form-label flex-shrink-zero">Manufacturer</a>
              <b-input v-model="manufacturerName"></b-input>
            </div>
            <div class="d-flex align-items-center mr-3">
              <a class="custom-form-label flex-shrink-zero">Source</a>
              <b-form-select v-model="sourceSelected" @change="searchResultMessage='Source changed, waiting for search.'">
                <option :value="1">Offers</option>
                <option :value="2">Inventory</option>
                <option :value="3" v-if="!isPurchaseOrderLineView"
                  >On Order</option
                >
              </b-form-select>
            </div>
            <div
              class="d-flex align-items-center mr-3"
            >
              <a class="custom-form-label flex-shrink-zero">Vendor</a>
              <b-input v-model="vendor"></b-input>
            </div>

            <b-button @click="onSearch" size="sm" variant="primary" :disabled="isSearching">
              <i v-show="isSearching" class="fa fa-spinner fa-pulse fa-fw"></i>
              Search
            </b-button>
            <b-button @click="onInitialize" size="sm" class="ml-2"
              >Clear</b-button>
          </div>
        </div>
        <div>
          <b-alert variant="info" class="mt-1 text-center" show size="sm" v-if="searchResultMessage != null">
            <strong>{{ searchResultMessage }}</strong>
          </b-alert>
        </div>
        <offers
          v-if="sourceSelected === 1"
          :quoteId="quoteId"
          :filters="filters"
          :gridId="offersGrid"
          :modalOfferFormId="'item-requirements-offer-form-modal'"
          hideActions
          @selectedSourceObj="onSelectedSourceObj"
          @part-value="onNewOfferSaved"
          @searching="isSearching = $event"
        />
        <inventory-list
          v-else-if="sourceSelected === 2"
          hideActions
          :inventorySourceFilters="filters"
          :gridId="inventoryGrid"
          :showFilter="!isPurchaseOrderLineView"
          hidePrint
          hideReports
          @selectedSourceObj="onSelectedSourceObj"
          @searching="isSearching = $event"
        />
        <on-order
          v-else-if="sourceSelected === 3"
          hideActions
          :onOrderFilter="filters"
          :gridId="onOrderGrid"
          @selectedSourceObj="onSelectedSourceObj"
          @searching="isSearching = $event"
          isLinesFiltered
          isPOTab
        />

        <requirement-details
          ref="itemForm"
          v-if="selectedSource"
          addSources
          backgroundColor="#f6fbfd"
          :pageName="pageName"
          :lineObj="createReqObj"
          :ShowBuyPrice="!isPurchaseOrderLineView"
          :PriceLabel="isPurchaseOrderLineView ? 'Price' : 'Resale Price'"
          :CompanyPNLabel="isPurchaseOrderLineView ? 'Vendor PN' : 'Customer PN'"
          :offerPrice="isPurchaseOrderLineView && sourceSelected === 1 ? this.createReqObj.cost : 0"
          @partChange="onPartChange"
          @lineChange="onLineChange"
          @headerChange="onHeaderChange"
          @onSuggestSelect="onPartSelected"
          @form-validation="isValidForm = !($event)"
        />
      </div>
      <template v-slot:modal-footer>
        <b-button @click="$refs['item-sources-modal'].hide()">Cancel</b-button>
        <b-button
          variant="primary"
          @click="addSource"
          :disabled="isSaving || !canAddSource"
          >{{isSaving ? 'Saving...' : 'Save'}}</b-button
        >
      </template>
    </b-modal>

    <add-record
      :title="addButtonText"
      :fields="fieldsData"
      :resetModal="resetModal"
      :gridId="gridId"
      :saveRecord="saveQuoteLineItems"
      :navigationField="navigationField"
      :items="itemData"
      :showFilters="showFilters"
      :pageInfo="pageInfo"
      :sortMethod="getData"
      :getDataMethod="getData"
      @updateCheckedRowItemIds="checkedQuoteLineItemIds = $event"
      class="mr-2"
    >
      <template v-slot:subHeader v-if="isPurchaseOrderLineView">
        <h6>Please select one or more Sales Order Line item(s) and add to the Purchase Order</h6>
      </template>
      <template v-slot:subHeader v-else-if="isSalesOrderLineView">
        <h6>Please select one or more Quote Line item(s) and add to the Sales Order</h6>
      </template>
      <template v-slot:subHeader v-else-if="!isSalesOrderLineView && !isPurchaseOrderLineView">
        <h6>Please check one or more Requirement Line item(s) and add to the Quotes</h6>
      </template>
    </add-record>
    <b-button @click="openItemSourcesModal" size="sm" variant="primary">
      <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>
      <i v-else class="fa fa-plus mr-1" aria-hidden="true"></i>
      Add Item
    </b-button>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';

import { GRID_ID } from '@/shared/constants';

import itemRequirementsFields from './fields';
import { addSourceInventoryFields } from '@/shared/components/InventoryList/fields';
import { addSourceOffersFields } from '@/shared/components/Offers/fields';

import { required } from 'vuelidate/lib/validators';
import VueSimpleSuggest from 'vue-simple-suggest';

import AddRecord from '@/shared/components/AddRecord';
import Offers from '@/shared/components/Offers/index';
import InventoryList from '@/shared/components/InventoryList/index';
import OnOrder from '@/shared/components/OnOrder/index';
//import PurchaseOrders from '@/shared/components/PurchaseOrders/index';
import PurchaseOrderLines from '@/pages/PurchaseOrders/components/PurchaseOrderLines/index';
import RequirementDetails from '@/shared/components/RequirementDetails/index';
import soLineFields from '@/pages/PurchaseOrders/components/add/fields';
import quoteLineFields from '@/pages/Quotes/components/QuoteLines/fields';
import PartFields from '@/pages/Quotes/components/AddQuote/fields';
import RequirementLineFields from '@/pages/Requirements/fields';
import PreferredSources from '@/shared/components/Sources';


export default {
  props: {
    companyId: {
      type: [Number, String],
      default: 0,
    },
    contactId: {
      type: [Number, String],
      default: 0,
    },
    salesOrderId: {
      type: [Number, String],
      default: 0,
    },
    purchaseOrderId: {
      type: [Number, String],
      default: 0,
    },
    addText: {
      type: String,
      default: 'Requirement',
    },
    pageName: {
      type: String,
      default: '',
    },
    disableAddSource: {
      type: Boolean,
      default: false,
    },
    isSalesOrderLineView: {
      type: Boolean,
      default: false,
    },
    isPurchaseOrderLineView: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    VueSimpleSuggest,
    AddRecord,
    Offers,
    InventoryList,
    OnOrder,
    //PurchaseOrders,
    PurchaseOrderLines,
    RequirementDetails,
    PreferredSources
  },
  data() {
    return {
      fields: itemRequirementsFields,
      inventoryFields: addSourceInventoryFields,
      offersFields: addSourceOffersFields,
      partFields: PartFields,
      requirementLineFields: RequirementLineFields,
      soLineFields,
      offersGrid: GRID_ID.OFFERS_SOURCES,
      inventoryGrid: GRID_ID.INVENTORY_SOURCES,
      onOrderGrid: GRID_ID.ON_ORDER_SOURCES,
      selectedRowItemId: 0,
      quoteLine: {},
      componentKey: 0,
      mergedObj: {},
      partNumber: '',
      manufacturerName: '',
      companyName: '',
      createReqObj: {},
      sourceSelected: 1,
      filters: {},
      canAddSource: false,
      canAddRequirement: false,
      showFilters: {
        customer: false,
        contact: false,
      },
      quoteLineFields: quoteLineFields,
      checkedQuoteLineItemIds: [],
      skip: 0,
      take: 10,
      isSaving: false,
      selectedSource: false,
      isSearching: false,
      searchResultMessage: null,
      reqLinePreferredSrcs: {}, // holds key and value, for requirement line and its multiple sources, respectively
      creatingQuotesDto: {}, // holds dto for posting the data
      reqLineKeys: [],
      isValidForm: false,
      rLinePageInfo: {},
      qLinePageInfo: {},
      sLinePageInfo: {}
    };
  },
  computed: {
    ...mapGetters('requirements', {requirementList: 'gridList'}),
    ...mapGetters('quoteLines', {qLineList: 'gridList'}),
    ...mapGetters('salesOrderLines', {sLineList: 'gridList'}),
    ...mapGetters('quoteAdd', ['parts', 'filteredQuoteLines']),
    ...mapGetters('account', ['gridSettingsById']),
    ...mapGetters('quotes', ['companyQuoteLines']),
    ...mapGetters('salesOrderLines', ['salesOrderLinesByVendor']),
    ...mapState('offers', ['offersListFiltered']),
    ...mapState('inventory', [ 'inventoryListFiltered']),
    ...mapGetters('purchaseOrderLines', ['linesFiltered']),
    quoteId() {
      return parseInt(this.$route.params.id);
    },
    gridId() {
      return this.isSalesOrderLineView
        ? GRID_ID.SALES_ORDERS_ADD_GRID
        : this.isPurchaseOrderLineView
        ? GRID_ID.PURCHASE_ORDER_ADD_GRID
        : GRID_ID.REQUIREMENT_FOR_QUOTE;
    },
    gridSettings() {
      return this.gridSettingsById(this.gridId);
    },
    itemData() {
      return this.isSalesOrderLineView
        ? this.qLineList
        : this.isPurchaseOrderLineView
        ? this.sLineList
        : this.requirementList;
    },
    fieldsData() {
      return this.isSalesOrderLineView
        ? this.quoteLineFields
        : this.isPurchaseOrderLineView
        ? this.soLineFields
        : this.requirementLineFields;
    },
    addButtonText() {
      return 'Add ' + this.addText + ' Item';
    },
    navigationField() {
      return this.isPurchaseOrderLineView || this.isSalesOrderLineView ? 'partNumber' : 'id';
    },
    pageInfo() {
      return this.isSalesOrderLineView
        ? this.qLinePageInfo
        : this.isPurchaseOrderLineView
        ? this.sLinePageInfo
        : this.rLinePageInfo;
    }
    // filters() {
    //   return {
    //     partNumber: this.partNumber,
    //     manufacturerName: this.manufacturerName,
    //     className: this.className
    //   };
    // }
  },
  methods: {
    ...mapActions('itemRequirements', ['createWithReq']),
    ...mapActions('requirements', {getRequirements: 'getAll', sortRequirementGrid: 'sortGrid'}),
    ...mapActions('quoteLines', {getQuoteLines: 'getAll'}),
    ...mapActions('salesOrderLines', {getSalesOrderLines: 'getAll'}),
    ...mapActions('requirementAdd', ['getPartById', 'clearCurrentPart']),    
    ...mapActions('quotes', ['addFromRequirement']),
    ...mapActions('inventoryDetails', ['getPartDetails']),
    ...mapActions('offers', [
      'clearOffersListFiltered',
      'getEmptyVendorPartsList',
      'setClickedAddOfferFromQuotesId',
    ]),
    // ...mapActions('inventory', ['clearInventoryListFiltered']),
    ...mapMutations('requirements', {requirementSetItem: 'setItem', requirementSetFilter: 'setFilters'}),
    ...mapMutations('quoteLines', {qLineSetItem: 'setItem', qLineSetFilter: 'setFilters'}),
    ...mapMutations('salesOrderLines', {sLineSetItem: 'setItem', sLineSetFilter: 'setFilters'}),
    ...mapMutations('inventory', {inventorySetItem: 'setItem'}),
    ...mapActions('salesOrderLines', [
      'getBySalesOrderId',
      'addQuoteLineInSO',
      'createWithSOLine',
      'getSOLinesByVendorId',
    ]),
    ...mapActions('purchaseOrderLines', ['createWithPOLine']),
    ...mapActions('purchaseorders', ['savePurchaseOrderBySalesOrderLineIds']),
    ...mapActions('quoteAdd', [
      'getCustomerPreferredParts',
      'getCompanyQuoteLines',
    ]),
    onRowClick(row) {
      this.selectedRowItemId = row.dataItem.id;

      this.quoteLine = {
        id: 0,
        quoteId: this.quoteId,
        partId: row.dataItem.partId,
        buyerPrice: row.dataItem.buyerTargetPrice,
        price: row.dataItem.targetPrice,
        //quantity: row.dataItem.qtyOnHand,
      };

      if (!this.canAddRequirement) this.canAddRequirement = true;
    },
    async openItemSourcesModal() {
      this.$emit('addItemClick');
      this.$refs['item-sources-modal'].show();
    },
    addItemClick() {
      this.$emit('addItemClick');
    },
    async addSource() {
      try {
        await this.$refs.itemForm.getFormValidation();
        if (this.isValidForm) {
          this.isSaving = true;
          this.$refs['item-sources-modal'].hide()
          const fullObj = {...this.createReqObj, ...this.mergedObj, sourceId: this.sourceSelected};
          if (this.isSalesOrderLineView) {
            const response = await this.createWithSOLine(fullObj);
            if (response) this.$emit('link-action', response);
          } else if (this.isPurchaseOrderLineView) {
            fullObj.purchaseOrderId = this.purchaseOrderId;
            await this.createWithPOLine(fullObj);
          } else {
            const response = await this.createWithReq(fullObj);
            if (response) this.$emit('link-action', response);
          }
          this.isSaving = false;
          this.$emit('updateLineItem');
        }
      } catch (e) {
        this.isSaving = false;
      }
    },
    async onSelectedSourceObj(obj) {
      this.selectedSource = true;
      this.createReqObj = {
        ...this.createReqObj,
        ...obj,
        sourceItemId: obj.id,
        companyId: obj.companyId || 0,
        conditionId: (obj.conditionId != null) ? obj.conditionId : 0,
        requirementLineId: (obj.requirementLineId != null) ? obj.requirementLineId : 0,
        quantity: this.sourceSelected == 2 ? obj.available : obj.quantity,
        price: null,
        buyPrice: this.sourceSelected == 1 || this.sourceSelected == 2 ? obj.cost : obj.price,
        // price: this.sourceSelected == 1 || this.sourceSelected == 2 ? obj.cost : obj.price,
        cost: this.sourceSelected == 3 ? obj.price : obj.cost
      };

      if (this.isSalesOrderLineView) {
        this.createReqObj.salesOrderId = this.salesOrderId;
      } else if (this.isPurchaseOrderLineView) {
        this.createReqObj.purchaseOrderId = this.purchaseOrderId;
      } else {
        this.createReqObj.quoteId = this.quoteId;
      }

      this.getPartById(this.createReqObj.partId);

      if (!this.canAddSource) this.canAddSource = true;
    },
    onPartChange(partObj) {
      console.log(partObj, 'Triggered Part Change');
      this.mergedObj = {
        ...this.mergedObj,
        ...partObj,
      };
    },
    onLineChange(lineObj) {
      this.mergedObj = {
        ...this.mergedObj,
        ...lineObj,
      };
    },
    onHeaderChange(headerObj) {
      this.mergedObj = {
        ...this.mergedObj,
        ...headerObj,
      };
    },
    onPartSelected(partSelected) {
      this.partNumber = partSelected.partNumber;
      this.onSearch();
    },

    onSearch()
     {
      if (!this.partNumber && !this.vendor && !this.manufacturerName) 
      {
        alert('Please enter a part number or manufacturer to search available sources.');
      }
      this.searchResultMessage = "Performing search...";

      let filterVal = 
      {
        partNumber : this.partNumber,
        companyName: this.vendor
      };

      if (this.isPurchaseOrderLineView) 
      {
        filterVal.companyId = this.companyId;
      }

      if (this.sourceSelected === 1)
      {
        //If offer grid
        filterVal.mfr = this.manufacturerName;
      } 
      else if (this.sourceSelected == 2) 
      {
        // If inventory
        if (this.isPurchaseOrderLineView) 
        {
          filterVal.sourceId = 3;              // only get the vendor excess inventory sources
          filterVal.companyId = this.companyId // excess from only this vendor  
        }
        filterVal.manufacturerName = this.manufacturerName;
        filterVal.isAvailable = true;
      } 
      else if (this.sourceSelected == 3) 
      {
        // If on orders
        filterVal.mfgName = this.manufacturerName;
        filterVal.hasNoSalesOrderLine = true;
      }

      this.filters = filterVal;
    },
    onInitialize() {
      this.selectedSource = false;
      this.createReqObj = {};
      this.partNumber = '';
      this.companyName = '';
      this.manufacturerName = '';
      this.searchResultMessage = null;

      this.canAddSource = false;
      this.sourceSelected = 1;

      this.clearOffersListFiltered();
      // this.clearInventoryListFiltered();
      this.inventorySetItem({ resource:'inventoryListFiltered', payload: [] });
      this.clearCurrentPart();
    },
    resetModal() {
      this.checkedQuoteLineItemIds = [];
    },
    async saveQuoteLineItems() {
      if (this.checkedQuoteLineItemIds.length > 0) {
        if (this.isSalesOrderLineView) {
          await this.addQuoteLineInSO({
            salesOrderId: this.salesOrderId,
            quoteLinesIds: this.checkedQuoteLineItemIds,
          });
        } else if (this.isPurchaseOrderLineView) {
          await this.savePurchaseOrderBySalesOrderLineIds({
            purchaseOrderId: this.purchaseOrderId,
            salesOrderLineIds: this.checkedQuoteLineItemIds,
          });
        } else {
          for(let i in this.checkedQuoteLineItemIds) {
            await this.addFromRequirement({
              quoteId: this.quoteId,
              requirementLineId: this.checkedQuoteLineItemIds[i]
            });
          }
        }

        this.$emit('updateLineItem');

        this.resetModal();  
      }
      
    },
    async getSalesOrderLine() {
      if (this.salesOrderId) {
        await this.getBySalesOrderId(this.salesOrderId);
      }
    },
    sortGrid($evt) {
      console.log($evt, "Event")
      if (this.isSalesOrderLineView) {

      } else if (this.isPurchaseOrderLineView) {

      } else {
        
      }        
    },
    async getData(params = null) {
      if (this.companyId > 0) {
        if (this.isSalesOrderLineView) {
          if (params != null)
            this.qLinePageInfo = { ...this.qLinePageInfo, ...params };
          else 
            this.qLinePageInfo = { ...this.qLinePageInfo, pageNumber: 1 };
          
          const { paging} = await this.getQuoteLines({
            ...this.qLinePageInfo,
            filters: {
              companyId: this.companyId,
              statuses: [206]
            },
            isSavePageInfo: false
          });
          this.qLinePageInfo = { ...this.qLinePageInfo, ...paging };


        } else if (this.isPurchaseOrderLineView) {
          if (params != null)
            this.sLinePageInfo = { ...this.sLinePageInfo, ...params };
          else 
            this.sLinePageInfo = { ...this.sLinePageInfo, pageNumber: 1 };
          
          const { paging } = await this.getSalesOrderLines({
            ...this.sLinePageInfo,
            filters: {
              vendorId: this.companyId,
              isVendorSource: true
            },
            isSavePageInfo: false
          });
          this.sLinePageInfo = { ...this.sLinePageInfo, ...paging };
        } else {
          if (params != null)
            this.rLinePageInfo = { ...this.rLinePageInfo, ...params };
          else 
            this.rLinePageInfo = { ...this.rLinePageInfo, pageNumber: 1 };

          const { paging }  = await this.getRequirements({
            ...this.rLinePageInfo,
            filters: {
              companyId: this.companyId,
              isOpen: true
            },
            isSavePageInfo: false
          });
          this.rLinePageInfo = { ...this.rLinePageInfo, ...paging };
        }
      }
    },
    onNewOfferSaved(e) {
      this.sourceSelected = 1;
      this.partNumber = e;
      this.onSearch();
    }
  },
  watch: {
    // pageInfo(newPageInfo) {
    //   this.pageNumber = newPageInfo.pageNumber;
    //   this.take = newPageInfo.pageSize;
    // },
    companyId() {
      this.getData();
    },
    isSearching() {
      if (!this.isSearching) {
        let ctr = 0;

        switch(this.sourceSelected)
        {
          case 1: ctr = this.offersListFiltered.length; break;
          case 2: ctr = this.inventoryListFiltered.length; break;
          case 3: ctr = this.linesFiltered.length; break;
        }

        this.searchResultMessage = `Search returned (${ctr}) result(s).`
      }
    }
  },
  async mounted() {
    this.getData();
  },
  created() {
    let setting = { ...this.gridSettings.sort, ...this.gridSettings.pageSize }
    if (this.isSalesOrderLineView)
      this.qLinePageInfo = setting;
    else if (this.isPurchaseOrderLineView)
      this.sLinePageInfo = setting;
    else 
      this.rLinePageInfo = setting;
  }
};
</script>

<style></style>
